import type { pisti } from "@app/rules";
import type { seatSlots } from "./seatOrder";

export type Slots = ReturnType<typeof seatSlots>;

/** A jack's rank on the wire: it takes any pile, which the table says when it happens. */
export const JACK_RANK = 11;

/** Where a card comes from, and where a captured pile goes, by the seat's place at the table. */
export const TOWARD: Record<keyof Slots, { dx: number; dy: number }> = {
  seatTop: { dx: 0, dy: -90 },
  seatLeft: { dx: -110, dy: 0 },
  seatRight: { dx: 110, dy: 0 },
  seatSelf: { dx: 0, dy: 120 },
};

export function slotOf(slots: Slots, seat: number | null): keyof Slots | null {
  const keys = Object.keys(slots) as (keyof Slots)[];
  return keys.find((key) => slots[key] === seat) ?? null;
}

/** A small tilt per card, the same every time it is drawn: a pile is never square. */
export function tiltOf(label: string): number {
  let sum = 0;
  for (const char of label) sum += char.charCodeAt(0);
  return (sum % 17) - 8;
}

/** Whoever played the top card: the seat that took the pile, else the seat before the one to move. */
export function playerOfTop(view: pisti.PistiView): number | null {
  if (view.sweep !== null) return view.sweep.seat;
  // An empty table has no top card: whoever took the pile is named by "last taken by".
  if (view.pileTop === null) return null;
  const opening = view.faceDownCount > 0 && view.pileCount === view.faceDownCount + 1;
  return view.turn === null || opening ? null : (view.turn + 3) % 4;
}

/** The throw of a card onto the pile (`--animate-throw` in the ui tokens). */
export const THROW_MS = 480;

/** The sweep animation (`--animate-sweep` in the ui tokens). */
export const SWEEP_MS = 800;

export type SweepStage = "rest" | "flying" | "gone";

/**
 * Where a capture is `elapsedMs` after it was first seen: the two cards lying side by side, the
 * pile flying to its taker, or gone. The flight runs inside the server's pause, so the table is
 * clear the moment play goes on; once gone the pile is never drawn again, whatever the clock
 * (the trick's lesson of 13 Sept 2026). Pure.
 */
export function sweepStage(elapsedMs: number, pauseMs: number): SweepStage {
  const rest = Math.max(0, pauseMs - SWEEP_MS);
  if (elapsedMs < rest) return "rest";
  return elapsedMs < rest + SWEEP_MS ? "flying" : "gone";
}
